import React, { useRef, useState } from 'react';
import type { TextInput } from 'react-native';
import { Animated, Keyboard, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import useShake from 'src/presentation/hooks/useShake';
import Cta from 'src/presentation/components/atoms/Cta';
import FormField from 'src/presentation/components/atoms/FormField';
import PasswordField from 'src/presentation/components/atoms/PasswordField';
import SubmitButton from 'src/presentation/components/atoms/SubmitButton';
import KeyboardSpacer from 'src/presentation/components/atoms/KeyboardSpacer';
import Translations from 'src/core/utils/Translations';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';

interface LoginFormProps {
	profile?: boolean;
	onLogin: (routeName: string) => void;
	onResetPassword: (route: string) => void;
}

interface LoginErrors {
	email?: string;
	password?: string;
	form?: string;
}

function LoginForm({ profile, onLogin, onResetPassword }: LoginFormProps) {
	const authStore = useAuthenticationStore();
	const { validator } = useServicesStore();
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [errors, setErrors] = useState<LoginErrors>({});
	const passwordRef = useRef<TextInput>(null);
	const { translateX: buttonShake, shake } = useShake();

	const clearError = (field: 'email' | 'password') => {
		setErrors((current) =>
			current[field] || current.form ? { ...current, [field]: undefined, form: undefined } : current,
		);
	};

	const onSubmit = async () => {
		Keyboard.dismiss();

		// Required fields are checked locally first; the API call only follows for a valid form.
		const nextErrors: LoginErrors = {};
		if (!email.trim()) nextErrors.email = Translations.errorFieldRequired;
		else if (!validator.validateEmail({ email })) nextErrors.email = Translations.errorInvalidEmailInline;
		if (!password) nextErrors.password = Translations.errorFieldRequired;
		if (nextErrors.email || nextErrors.password) {
			setErrors(nextErrors);
			shake();
			return;
		}

		setErrors({});
		const response = await authStore.login(email, password);
		if (response.success) {
			onLogin(response.routeName ?? '');
		} else {
			// Show the specific message inline instead of a generic "something went wrong" alert.
			setErrors({ form: response.message ?? Translations.errorInvalidCredentials });
		}
	};

	const resetPasswordRoute = profile ? 'LostPasswordPage' : 'ForgotPassword';
	const isIos = Platform.OS === 'ios';

	return (
		<View>
			<FormField
				blurOnSubmit={false}
				error={errors.email}
				keyboardType="email-address"
				label={isIos ? Translations.labelEmail : Translations.labelEmailAndroid}
				placeholder={Translations.placeholderEmail}
				returnKeyType="next"
				value={email}
				onChangeText={(text) => {
					setEmail(text);
					clearError('email');
				}}
				onSubmitEditing={() => passwordRef.current?.focus()}
			/>

			<PasswordField
				error={errors.password}
				label={Translations.labelPassword}
				placeholder={Translations.placeholderPassword}
				ref={passwordRef}
				returnKeyType="go"
				value={password}
				onChangeText={(text) => {
					setPassword(text);
					clearError('password');
				}}
				onSubmitEditing={() => {
					onSubmit().catch(() => undefined);
				}}
			/>

			{errors.form ? <Text style={styles.formError}>{errors.form}</Text> : null}

			<TouchableOpacity style={styles.forgotPasswordBtn} onPress={() => onResetPassword(resetPasswordRoute)}>
				{isIos ? (
					<Cta
						buttonStyle="transparent"
						fontSize={Dimens.fontSize12}
						label={Translations.buttonForgotPassword}
						labelColor={Colors.secondary}
						upperCase={false}
					/>
				) : (
					<Text style={styles.resetPasswordLabel}>{Translations.buttonForgotPassword}</Text>
				)}
			</TouchableOpacity>

			{isIos ? (
				<Animated.View style={[styles.submitRow, { transform: [{ translateX: buttonShake }] }]}>
					<TouchableOpacity disabled={authStore.isLoading} onPress={onSubmit}>
						<Cta
							buttonStyle="primary"
							isLoading={authStore.isLoading}
							label={Translations.buttonLogin}
							labelColor={Colors.white}
							upperCase={true}
						/>
					</TouchableOpacity>
				</Animated.View>
			) : (
				<>
					<Animated.View style={{ transform: [{ translateX: buttonShake }] }}>
						<SubmitButton
							isLoading={authStore.isLoading}
							label={Translations.buttonLogin}
							style={styles.submitButtonAndroid}
							onPress={onSubmit}
						/>
					</Animated.View>
					<KeyboardSpacer />
				</>
			)}
		</View>
	);
}

export default LoginForm;

const styles = StyleSheet.create({
	forgotPasswordBtn: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		paddingHorizontal: Platform.OS === 'ios' ? 0 : Dimens.padding16,
	},
	submitRow: {
		flexDirection: 'row',
		justifyContent: 'center',
	},
	submitButtonAndroid: {
		marginTop: Dimens.margin32,
		flexDirection: 'row',
		justifyContent: 'center',
	},
	resetPasswordLabel: {
		color: Colors.secondary,
		fontSize: Dimens.fontSize12,
		fontFamily: Fonts.montserratRegular,
	},
	formError: {
		color: Colors.red,
		fontSize: Dimens.fontSize14,
		fontFamily: Fonts.montserratRegular,
		textAlign: 'center',
		marginHorizontal: Dimens.margin16,
		marginTop: Dimens.margin8,
	},
});
